/** Iter 632 — customer wizard · paid/done card (JSX moved verbatim from EngageSessionPanel.jsx). */
import React from "react";
import { CheckCircle2, Star } from "lucide-react";
import SocialPostHelper from "@/components/engage/SocialPostHelper";
import WizardBar from "@/features/customerWizard/WizardBar";
import PhotoStep from "@/features/customerWizard/PhotoStep";
import ShareStep from "@/features/customerWizard/ShareStep";
import StreakMilestoneCard from "@/features/customerWizard/StreakMilestoneCard";
import CelebrationOverlay from "@/features/customerWizard/CelebrationOverlay";
import { money } from "@/features/customerWizard/helpers";

export default function PaidCard({ w }) {
  const {
    s, sessionId, pendingEntry, payStatusQ, photo, setPhoto, proofLocal, setProofLocal,
    platformsQ, qc, vendorRef, recoverAttempts, setExpired, setSessionId, setPayChoice,
    pointsOnlyCalled, setUsePoints, setExtraPhotos, setWaiting,
  } = w;
  // Iter 647 — celebration overlay: only when THIS visit's payment-status
  // poll returned the celebration payload (a plain resume of an old paid
  // session never polls, so it never re-celebrates). Continue dismisses;
  // a wizard reset unmounts PaidCard which clears the flag naturally.
  const celebration = payStatusQ.data?.celebration || null;
  const [celebrated, setCelebrated] = React.useState(false);
  // Iter-330 — the earn-back banner previously keyed off a wrong field
  // (`session_status`) so a paid session with the review window open
  // rendered ONLY the paid card: the "Earn your discount back" link
  // from My visits dead-ended. The shared backend exposes `status`
  // (same enum as /sessions/mine); `session_status` kept as fallback.
  const reviewOutstanding =
    s?.status === "payment_only_pending_review_window" ||
    s?.session_status === "payment_only_pending_review_window" ||
    !s ||
    ["required", "skipped", "pending"].includes(s.review_state);
  const deadlineSrc = pendingEntry?.review_deadline || s?.review_deadline;
  const deadline = deadlineSrc
    ? new Date(deadlineSrc).toLocaleString("en-NZ", {
        weekday: "short", hour: "numeric", minute: "2-digit", day: "numeric", month: "short",
      })
    : null;
  const points = pendingEntry?.potential_points ?? null;
  const credited =
    s?.status === "resolved_points_credited" ||
    s?.session_status === "resolved_points_credited";
  const creditedPoints = s?.points_credited ?? s?.potential_points ?? points;
  return (
    <div>
      {celebration && !celebrated && (
        <CelebrationOverlay c={celebration} onClose={() => setCelebrated(true)} shareUrl={s?.vendor?.share_url} />
      )}
      <WizardBar step={6} />
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="engage-session-paid">
      <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-600" />
      <div className="mt-2 font-display text-xl font-black text-slate-900">🎉 Paid — you&apos;re all set</div>
      <p className="mt-1 text-sm text-slate-600">Show this screen at the till if they ask for confirmation.</p>
      {/* Iter-600 §2 — gold streak cheer on the done screen. */}
      <StreakMilestoneCard />
      {/* Iter-357 §3 — customer receipt: total paid, points used (when
          present), payment ref, date. No codes, no internal fields. */}
      {(() => {
        const tx = payStatusQ.data?.transaction || {};
        const paidTotal = money(tx.charge_total ?? s?.charge_total ?? s?.amounts?.charge_total);
        const paidAtSrc = tx.paid_at || s?.paid_at;
        const paidAt = paidAtSrc
          ? new Date(paidAtSrc).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })
          : null;
        const ref = tx.receipt_ref || tx.payment_ref || s?.receipt_ref || null;
        if (!paidTotal && !ref && !paidAt) return null;
        return (
          <div className="mt-3 text-sm text-slate-700" data-testid="engage-paid-receipt">
            {paidTotal && (
              <div className="font-black text-slate-900">
                Paid {paidTotal}
                {paidAt && <span className="font-semibold text-slate-500"> on {paidAt}</span>}
              </div>
            )}
            {ref && <div className="mt-0.5 text-xs text-slate-500">Receipt {ref}</div>}
          </div>
        );
      })()}
      {/* Iter-333 — receipt line when part of the bill was paid with points */}
      {(() => {
        const ptsDiscount = Number(s?.points_discount_nzd ?? payStatusQ.data?.points_discount_nzd) || 0;
        if (ptsDiscount <= 0) return null;
        return (
          <div className="mt-2 text-sm font-bold text-emerald-700" data-testid="engage-paid-points-used">
            Points used −{money(ptsDiscount)}
          </div>
        );
      })()}
      {/* Iter 681 — owner approved the post (one-tap link, dashboard or
          till): deferred points landed. Confetti cheer per WO spec. */}
      {credited && (
        <div
          className="mt-4 rounded-xl bg-gradient-to-br from-amber-50 to-emerald-50 border border-emerald-300 p-5 text-center"
          data-testid="cust-points-landed"
        >
          <div className="text-2xl" aria-hidden="true">🎉</div>
          <div className="mt-1 font-display text-lg font-black text-slate-900">
            {creditedPoints != null
              ? `${creditedPoints} reward points just landed!`
              : "Your reward points just landed!"}
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {s?.vendor?.business_name || "The store"} approved your post. Spend them next visit.
          </p>
        </div>
      )}
      {/* Iter-358 follow-up (approved): caption composer stays available
          after payment even when no review is outstanding — reuses the
          session-scoped post-suggestions drafts. The outstanding-review
          branch uses the Iter-558 Photo → Share screens instead. */}
      {!reviewOutstanding && sessionId && (
        <div className="mt-4 rounded-xl bg-white border border-slate-200 p-4 text-left" data-testid="engage-paid-share-card">
          <div className="text-sm font-bold text-slate-900">Share your visit</div>
          <p className="mt-0.5 text-xs text-slate-600">
            Post about {s?.vendor?.business_name || "the store"} — grab a ready-made caption below.
          </p>
          <SocialPostHelper sessionId={sessionId} links={s?.vendor?.social_links} platform="instagram" vendorId={s?.vendor?.id || s?.vendor_id} photoB64={photo?.b64} shareUrl={s?.vendor?.share_url} />
        </div>
      )}
      {!credited && reviewOutstanding && (
        <div className="mt-4 rounded-xl bg-white border border-emerald-200 p-4 text-left" data-testid="engage-earnback-banner">
          <div className="flex items-start gap-2.5">
            <Star className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-slate-900">
                Earn your discount back — you have 48 hours
              </div>
              <p className="mt-0.5 text-xs text-slate-600 leading-relaxed" data-testid="engage-earnback-copy">
                Leave the store a review and send us the proof below
                {points != null ? ` to earn ${points} points` : " to get your discount credited back"}.
                {deadline ? ` Post before ${deadline}.` : ""}
              </p>
            </div>
          </div>
          {/* Iter-558 — returning 48h customer posts via the same
              Photo → Share screens (deadline banner above). */}
          {sessionId && (
            proofLocal ? (
              <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-emerald-700" data-testid="engage-earnback-submitted">
                <CheckCircle2 className="w-4 h-4" />
                {proofLocal === "verified"
                  ? "Post verified — points on the way!"
                  : "Proof received — the store will approve it, automatically within 48 hours if they don't respond."}
              </div>
            ) : !photo ? (
              <div className="mt-3">
                <PhotoStep photo={photo} onPhoto={setPhoto} skipChosen={false} />
              </div>
            ) : (
              <div className="mt-3">
                <ShareStep
                  sessionId={sessionId}
                  photo={photo}
                  onRetake={() => {
                    if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
                    setPhoto(null);
                  }}
                  socialLinks={s?.vendor?.social_links}
                  platforms={platformsQ.data?.platforms || null}
                  onProofDone={(status) => {
                    setProofLocal(status);
                    qc.invalidateQueries({ queryKey: ["engage-session", sessionId] });
                    qc.invalidateQueries({ queryKey: ["pending-reviews"] });
                  }}
                />
              </div>
            )
          )}
        </div>
      )}
      <button
        type="button"
        data-testid="engage-start-new-visit"
        onClick={() => {
          recoverAttempts.current = 0;
          setExpired(false);
          setSessionId(null);
          // Iter-621 — a new visit restarts the pay-choice flow.
          setPayChoice(null);
          pointsOnlyCalled.current = false;
          setUsePoints(false);
          setExtraPhotos([]);
          setWaiting("Ask the staff to start your sale at the till");
        }}
        className="mt-4 inline-flex items-center justify-center rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-6 py-2.5 transition-colors"
      >
        Start a new visit
      </button>
      </div>
    </div>
  );
}
